import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Videocam,
  Chat as ChatIcon,
  Refresh,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import ReplayModal from '../../components/Replay/ReplayModal';
import { API_URL } from '../../lib/constants';
import { useSearchParams } from 'react-router-dom';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { canAccessPersona, isSuperAdmin } from '@common/domain/auth';
import { getChatSession } from '../../lib/api/Chat';
import { GetSessionRes } from '../../lib/models/Chat';
import { Exchange } from '../../lib/interfaces/Chat';
import { MarkdownContent } from '../../components/Chat/MarkdownContent';
import { ClassificationBadge, SafetyRoute } from '../../components/ClassificationBadge/ClassificationBadge';
import { WebSearchBadge } from '../../components/Chat/WebSearchBadge';

const CopyIconButton: React.FC<{
  textToCopy: string;
  tooltipTitle?: string;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}> = ({ textToCopy, tooltipTitle = 'Copy', size = 'small', sx }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const label = copied ? 'Copied!' : tooltipTitle;

  return (
    <Tooltip title={label} placement="top">
      <IconButton size={size} onClick={handleCopy} sx={sx} aria-label={label}>
        {copied ? <CheckIcon fontSize={size} color="success" /> : <ContentCopyIcon fontSize={size} />}
      </IconButton>
    </Tooltip>
  );
};

interface TranscriptSessionEntry {
  chatId: string;
  userId: string;
  userEmail?: string;
  personaId: string;
  personaName?: string;
  category?: string;
  tenantId?: string;
  title?: string;
  createdAt: string;
  updatedAt?: string;
  chatType?: string;
  surface: 'AVATAR' | 'TEXT';
  isConversation?: boolean;
}

const isWebSearchResponseMode = (mode: string | undefined | null): boolean => {
  if (mode == null || typeof mode !== 'string') return false;
  return mode.trim().toLowerCase().replace(/_/g, '') === 'websearch';
};

const parseExchangeMeta = (exchange: Exchange) => {
  let safetyRoute: SafetyRoute | undefined;
  let responseMode: string | undefined;

  const rawMeta = exchange.metadata ?? exchange.workflowMetadata;
  if (rawMeta) {
    let metaObj: any = rawMeta;
    if (typeof rawMeta === 'string') {
      try {
        metaObj = JSON.parse(rawMeta);
      } catch (e) {
        metaObj = null;
      }
    }
    if (metaObj) {
      if (metaObj.safetyClassification?.route) {
        safetyRoute = metaObj.safetyClassification.route as SafetyRoute;
      } else if (metaObj.safetyRoute) {
        safetyRoute = metaObj.safetyRoute as SafetyRoute;
      }
      if (metaObj.responseMode) {
        responseMode = String(metaObj.responseMode);
      }
    }
  }

  return { safetyRoute, responseMode };
};

const useSearchParamsSafe = (): [URLSearchParams, (params: URLSearchParams) => void] => {
  try {
    return useSearchParams();
  } catch {
    const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const setSearchParams = (params: URLSearchParams) => {
      if (typeof window !== 'undefined') {
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
      }
    };
    return [searchParams, setSearchParams];
  }
};

export interface TranscriptsTabProps {
  defaultPersonaId?: string;
}

const TranscriptsTab: React.FC<TranscriptsTabProps> = ({ defaultPersonaId }) => {
  const { userInfo } = useUserInfoContext();

  // Filter States
  const [personas, setPersonas] = useState<{ id: string; name: string; category?: string; tenantId?: string }[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState(defaultPersonaId || 'all');
  const [modeFilter, setModeFilter] = useState<'all' | 'AVATAR' | 'TEXT'>('all');
  const [searchParams, setSearchParams] = useSearchParamsSafe();
  const initialChatIdParam = searchParams.get('chatId') || '';
  const initialUserParam = searchParams.get('user') || '';

  const [chatIdFilter, setChatIdFilter] = useState(initialChatIdParam);
  const [userFilterInput, setUserFilterInput] = useState(initialUserParam);
  const [debouncedUserFilter, setDebouncedUserFilter] = useState(initialUserParam);
  const [dateRange, setDateRange] = useState(initialChatIdParam ? 'all' : '30days');
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedUserFilter(userFilterInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [userFilterInput]);

  // Data States
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TranscriptSessionEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  // Modal / Detail States
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<GetSessionRes | null>(null);
  const [inspectingSessionEntry, setInspectingSessionEntry] = useState<TranscriptSessionEntry | null>(null);
  const [fullTranscriptCopied, setFullTranscriptCopied] = useState(false);

  useEffect(() => {
    if (!fullTranscriptCopied) return;
    const timer = setTimeout(() => setFullTranscriptCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [fullTranscriptCopied]);

  // Replay Modal State
  const [replayModalOpen, setReplayModalOpen] = useState(false);
  const [replaySession, setReplaySession] = useState<GetSessionRes | null>(null);
  const [replaySessionEntry, setReplaySessionEntry] = useState<TranscriptSessionEntry | null>(null);
  const [replayLoadingId, setReplayLoadingId] = useState<string | null>(null);

  useEffect(() => {
    if (userInfo?.accessToken) {
      fetchPersonas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.accessToken]);

  useEffect(() => {
    if (!userInfo?.accessToken) return;
    const controller = new AbortController();
    fetchTranscripts(controller.signal);
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPersonaId, modeFilter, dateRange, customStartDate, customEndDate, debouncedUserFilter, chatIdFilter, page, personas, userInfo?.accessToken]);

  const fetchPersonas = async () => {
    if (!userInfo?.accessToken) return;
    try {
      const response = await fetch(`${API_URL}/personas`, {
        headers: { Authorization: `Bearer ${userInfo.accessToken}` },
      });
      const result = await response.json();
      const isSuper = isSuperAdmin(userInfo?.groups);
      const filtered = Array.isArray(result)
        ? (isSuper ? result : result.filter((p) => canAccessPersona(p, userInfo)))
        : [];
      setPersonas(filtered);
    } catch (err) {
      console.error('Failed to fetch personas for transcripts', err);
    }
  };

  const fetchTranscripts = async (signal?: AbortSignal) => {
    if (!userInfo?.accessToken) return;
    setLoading(true);
    setFetchError(null);
    try {
      // Determine accessible persona IDs for site scoping
      const accessiblePersonaIds = personas.map((p) => p.id);

      let url = `${API_URL}/admin/transcripts?limit=${rowsPerPage}`;
      if (selectedPersonaId !== 'all') {
        url += `&personaId=${selectedPersonaId}`;
      } else if (accessiblePersonaIds.length > 0) {
        url += `&personaIds=${encodeURIComponent(accessiblePersonaIds.join(','))}`;
      }

      if (modeFilter !== 'all') url += `&mode=${modeFilter}`;
      if (debouncedUserFilter.trim()) url += `&user=${encodeURIComponent(debouncedUserFilter.trim())}`;
      if (chatIdFilter.trim()) url += `&search=${encodeURIComponent(chatIdFilter.trim())}`;

      const presetDays: Record<string, number> = { '7days': 7, '30days': 30, '90days': 90 };
      if (dateRange in presetDays) {
        const start = new Date(Date.now() - presetDays[dateRange] * 24 * 60 * 60 * 1000).toISOString();
        const end = new Date().toISOString();
        url += `&startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}`;
      } else if (dateRange === 'custom' && customStartDate && customEndDate) {
        const startIso = new Date(customStartDate).toISOString();
        const endDateObj = new Date(customEndDate);
        endDateObj.setHours(23, 59, 59, 999);
        url += `&startDate=${encodeURIComponent(startIso)}&endDate=${encodeURIComponent(endDateObj.toISOString())}`;
      }

      if (page > 0) {
        const cursorObj = { index: page * rowsPerPage };
        url += `&cursor=${btoa(JSON.stringify(cursorObj))}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${userInfo?.accessToken}` },
        signal,
      });
      if (signal?.aborted) return;
      const data = await response.json();
      if (signal?.aborted) return;

      if (response.ok) {
        const fetchedSessions: TranscriptSessionEntry[] = data.entries || [];
        setSessions(fetchedSessions);
        setTotalCount(data.totalCount || 0);

        // Auto-open transcript modal if filtered by a specific chatId for the first time
        if (chatIdFilter.trim() && !hasAutoOpened && fetchedSessions.length > 0) {
          const match = fetchedSessions.find((s) => s.chatId === chatIdFilter.trim()) || fetchedSessions[0];
          setHasAutoOpened(true);
          handleOpenSession(match);
        }
      } else {
        console.error('Failed to fetch transcripts:', data);
        setSessions([]);
        setTotalCount(0);
        setFetchError(data?.error || `Failed to fetch transcripts (${response.status})`);
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('Failed to fetch transcripts', err);
      setSessions([]);
      setTotalCount(0);
      setFetchError(err?.message || 'Failed to load transcripts due to a network or authentication error.');
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const handleOpenSession = async (entry: TranscriptSessionEntry) => {
    if (!userInfo) return;
    setInspectingSessionEntry(entry);
    setSelectedSession(null);
    setModalError(null);
    setModalOpen(true);
    setModalLoading(true);

    try {
      const session = await getChatSession(userInfo, entry.chatId, entry.userId);
      if (session) {
        setSelectedSession(session);
      } else {
        setModalError('Conversation not found. The session may have expired or the chat ID may be invalid.');
      }
    } catch (err: any) {
      console.error('Failed to fetch conversation session', err);
      const status = err?.response?.status;
      if (status === 403) {
        setModalError('Access denied. Your account may not have permission to view this conversation.');
      } else if (status === 404) {
        setModalError('Conversation not found. The session may have expired.');
      } else {
        setModalError(`Failed to load conversation: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleStartReplay = async (entry: TranscriptSessionEntry, existingSession?: GetSessionRes | null) => {
    if (!userInfo) return;
    setReplaySessionEntry(entry);
    setReplayLoadingId(entry.chatId);

    try {
      let sessionToReplay = existingSession;
      if (!sessionToReplay) {
        sessionToReplay = await getChatSession(userInfo, entry.chatId, entry.userId);
      }
      if (sessionToReplay && sessionToReplay.conversation) {
        setReplaySession(sessionToReplay);
        setReplayModalOpen(true);
      } else {
        setModalError('Failed to load conversation transcript for replay.');
      }
    } catch (err: any) {
      console.error('Failed to load session for replay:', err);
      setModalError(`Error loading transcript for replay: ${err?.message || 'Unknown error'}`);
    } finally {
      setReplayLoadingId(null);
    }
  };

  const personaMap = useMemo(() => {
    const map: Record<string, string> = {};
    personas.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [personas]);

  return (
    <Box p={3} sx={{ position: 'relative', height: '100%', overflow: 'auto' }}>
      {loading && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1100 }}>
          <LinearProgress />
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Recent Conversation Transcripts</Typography>
        <Button
          startIcon={<Refresh />}
          variant="outlined"
          onClick={() => fetchTranscripts()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Search & Filter Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        {chatIdFilter && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Active Filter:</Typography>
            <Chip
              label={`Chat ID: ${chatIdFilter}`}
              color="primary"
              variant="outlined"
              onDelete={() => {
                setChatIdFilter('');
                setHasAutoOpened(false);
                setPage(0);
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete('chatId');
                setSearchParams(nextParams);
              }}
            />
          </Box>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search User ID or Email"
              value={userFilterInput}
              onChange={(e) => {
                setUserFilterInput(e.target.value);
                setPage(0);
              }}
              placeholder="e.g. user@example.com"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Persona</InputLabel>
              <Select
                value={selectedPersonaId}
                label="Persona"
                onChange={(e) => {
                  setSelectedPersonaId(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Accessible Personas</MenuItem>
                {[...personas]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Surface</InputLabel>
              <Select
                value={modeFilter}
                label="Surface"
                onChange={(e) => {
                  setModeFilter(e.target.value as 'all' | 'AVATAR' | 'TEXT');
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Surfaces</MenuItem>
                <MenuItem value="AVATAR">Avatar Video Call</MenuItem>
                <MenuItem value="TEXT">Text Chat</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="7days">Last 7 Days</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="90days">Last 90 Days</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {dateRange === 'custom' && (
            <>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={customStartDate}
                  onChange={(e) => {
                    setCustomStartDate(e.target.value);
                    setPage(0);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={customEndDate}
                  onChange={(e) => {
                    setCustomEndDate(e.target.value);
                    setPage(0);
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Transcripts Table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center">User</TableCell>
              <TableCell align="center">Persona</TableCell>
              <TableCell align="center">Surface</TableCell>
              <TableCell align="center">Chat ID</TableCell>
              <TableCell align="center">Date & Time</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color={fetchError ? "error" : "text.secondary"}>
                    {loading
                      ? 'Loading transcripts...'
                      : fetchError
                      ? `Error loading transcripts: ${fetchError}`
                      : 'No conversation transcripts found for the selected filters. If expecting recent conversations, try adjusting the Persona or Date Range filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((s) => (
                <TableRow key={`${s.userId}-${s.chatId}`} hover>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      {s.userEmail ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {s.userEmail}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              ID: {s.userId}
                            </Typography>
                            {s.userId && (
                              <CopyIconButton textToCopy={s.userId} tooltipTitle={`Copy User ID: ${s.userId}`} size="small" />
                            )}
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {s.userId}
                          </Typography>
                          {s.userId && (
                            <CopyIconButton textToCopy={s.userId} tooltipTitle={`Copy User ID: ${s.userId}`} size="small" />
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{s.personaName || personaMap[s.personaId] || s.personaId}</Typography>
                      {s.category && (
                        <Chip label={s.category} size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {s.surface === 'AVATAR' ? (
                        <Chip
                          icon={<Videocam fontSize="small" />}
                          label="Avatar Call"
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<ChatIcon fontSize="small" />}
                          label="Text Chat"
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {s.chatId.length > 16 ? `${s.chatId.substring(0, 16)}...` : s.chatId}
                      </Typography>
                      <CopyIconButton textToCopy={s.chatId} tooltipTitle={`Copy Chat ID: ${s.chatId}`} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {new Date(s.updatedAt || s.createdAt).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenSession(s)}
                      >
                        View Transcript
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<ReplayIcon />}
                        onClick={() => handleStartReplay(s)}
                        disabled={replayLoadingId === s.chatId}
                      >
                        {replayLoadingId === s.chatId ? 'Loading...' : 'Replay'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[20]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
        />
      </TableContainer>

      {/* Transcript Detail Modal (Matches Text Chat presentation) */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '85vh', display: 'flex', flexDirection: 'column' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6">
              Transcript: {inspectingSessionEntry?.personaName || inspectingSessionEntry?.personaId}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                User: {inspectingSessionEntry?.userEmail ? `${inspectingSessionEntry.userEmail} (ID: ${inspectingSessionEntry.userId})` : inspectingSessionEntry?.userId}
              </Typography>
              {inspectingSessionEntry?.userId && (
                <CopyIconButton textToCopy={inspectingSessionEntry.userId} tooltipTitle="Copy User ID" size="small" />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
                |
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Chat ID: {inspectingSessionEntry?.chatId}
              </Typography>
              {inspectingSessionEntry?.chatId && (
                <CopyIconButton textToCopy={inspectingSessionEntry.chatId} tooltipTitle="Copy Chat ID" size="small" />
              )}
            </Box>
          </Box>
          <IconButton onClick={() => setModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {modalLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : modalError ? (
            <Box p={3} textAlign="center">
              <Typography color="error">{modalError}</Typography>
            </Box>
          ) : selectedSession?.conversation ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedSession.conversation.map((exchange: Exchange, index: number) => {
                const { safetyRoute, responseMode } = parseExchangeMeta(exchange);
                const personaDisplayName = inspectingSessionEntry?.personaName || 'Assistant';
                const questionText = exchange.question || '';
                const responseText = exchange.response || exchange.answer || '';
                const exchangeCopyText = questionText.trim()
                  ? `User: ${questionText}\n\n${personaDisplayName}: ${responseText}`
                  : `${personaDisplayName}: ${responseText}`;

                return (
                  <Box
                    key={exchange.exchangeId || (exchange as any).exchange_id || index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'action.hover',
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Exchange #{index + 1}
                      </Typography>
                      <CopyIconButton
                        textToCopy={exchangeCopyText}
                        tooltipTitle={`Copy Exchange #${index + 1}`}
                        size="small"
                      />
                    </Box>

                    {/* User Message Bubble */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        alignSelf: 'flex-end',
                        maxWidth: '85%',
                        boxShadow: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, display: 'block', mb: 0.5 }}>
                        User
                      </Typography>
                      <Typography variant="body2">{exchange.question}</Typography>
                    </Box>

                    {/* Badges - mirrors text chat */}
                    {(safetyRoute || isWebSearchResponseMode(responseMode)) && (
                      <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {safetyRoute && <ClassificationBadge safetyRoute={safetyRoute} />}
                        {isWebSearchResponseMode(responseMode) && <WebSearchBadge />}
                      </Box>
                    )}

                    {/* Assistant Message Bubble */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        alignSelf: 'flex-start',
                        maxWidth: '85%',
                        boxShadow: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {personaDisplayName}
                      </Typography>
                      <MarkdownContent content={exchange.response || exchange.answer || ''} sources={exchange.sources} />

                      {(exchange.timestamp || exchange.createdAt) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.75rem' }}>
                          {new Date(exchange.timestamp || exchange.createdAt!).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box p={3} textAlign="center">
              <Typography color="text.secondary">No conversation messages recorded in this transcript.</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={async () => {
              if (selectedSession?.conversation?.length) {
                const personaDisplayName = inspectingSessionEntry?.personaName || 'Assistant';
                const text = selectedSession.conversation
                  .map((e) => {
                    const q = (e.question || '').trim();
                    const r = e.response || e.answer || '';
                    return q ? `User: ${q}\n\n${personaDisplayName}: ${r}` : `${personaDisplayName}: ${r}`;
                  })
                  .join('\n\n---\n\n');
                try {
                  if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(text);
                    setFullTranscriptCopied(true);
                  }
                } catch (err) {
                  console.error('Failed to copy full transcript:', err);
                }
              }
            }}
            startIcon={fullTranscriptCopied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
            disabled={!selectedSession?.conversation?.length}
          >
            {fullTranscriptCopied ? 'Copied Full Transcript!' : 'Copy Full Transcript'}
          </Button>
          {inspectingSessionEntry && selectedSession && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ReplayIcon />}
              onClick={() => {
                setModalOpen(false);
                handleStartReplay(inspectingSessionEntry, selectedSession);
              }}
            >
              Replay Conversation
            </Button>
          )}
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Interactive Replay Modal */}
      {replayModalOpen && replaySessionEntry && replaySession && (
        <ReplayModal
          open={replayModalOpen}
          onClose={() => setReplayModalOpen(false)}
          originalChatId={replaySessionEntry.chatId}
          originalUserId={replaySessionEntry.userId}
          originalUserEmail={replaySessionEntry.userEmail}
          originalPersonaId={replaySessionEntry.personaId}
          originalPersonaName={replaySessionEntry.personaName}
          originalTitle={replaySessionEntry.title}
          originalConversation={replaySession.conversation || []}
          personas={personas}
        />
      )}
    </Box>
  );
};

export default TranscriptsTab;
